import { Component, signal, OnInit, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { PostMessageService } from './services/post-message.service';
import { AppModeService } from './services/app-mode-service';
import { AppLoader } from './core/components/app-loader/app-loader';
import { EnvironmentBannerComponent } from './core/components/environment-banner/environment-banner';
import { environment } from '@/environments/environment';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, AppLoader, EnvironmentBannerComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  standalone: true,
})
export class App implements OnInit {
  protected readonly title = signal('selfcare-embed');

  private appModeService = inject(AppModeService);
  private postMessageService = inject(PostMessageService);

  protected sessionExpired = this.postMessageService.sessionExpired;

  ngOnInit(): void {
    // If we have resume params and are nested inside a Yuno/external iframe,
    // break out to the outer selfcare iframe so the flow resumes in the correct context.
    const params = new URLSearchParams(window.location.search);
    const RESUME_PARAMS = ['resume', 'transactionId', 'paymentId', 'payment_id', 'id', 'payment_status', 'status'];
    const isResumeContext = RESUME_PARAMS.some((p) => params.has(p));
    const isNestedIframe = window.parent !== window && window.parent !== window.top;
    if (isResumeContext && isNestedIframe) {
      // 1. Notify vmco-web-selfcare (window.top) via postMessage so it reloads
      //    the selfcare-widget iframe with the resume params.
      try {
        window.top!.postMessage(
          { type: 'sc:gateway-redirect', url: window.location.href },
          '*'
        );
      } catch {
        // blocked — e.g. sandboxed top frame
      }

      // 2. Same-origin fallback: navigate the direct parent (original microapp
      //    iframe) to the resume URL so tryResumeEsimFlow() runs there.
      try {
        window.parent.location.href = window.location.href;
        return; // navigation dispatched — this frame becomes irrelevant
      } catch {
        // cross-origin parent — fall through to normal init so at least this
        // frame picks up the resume params.
      }
    }

    // Exponer bridge global para que la app nativa inyecte un token fresco:
    //   Android: webView.evaluateJavascript("window.injectToken('newJwt')", null)
    //   iOS:     webView.evaluateJavaScript("window.injectToken('newJwt')")
    (window as any).injectToken = (token: string) => this.postMessageService.injectToken(token);

    // Enviar "sc:ready" solo si estamos en modo embed
    setTimeout(() => {
      if (this.appModeService.isEmbedded()) {
        !environment.production &&
          console.log('[App] 🚀 Angular inicializado, enviando sc:ready al parent iframe...');
        this.postMessageService.notifyReady(); // <-- le habla al host
      } else {
        !environment.production &&
          console.log('[App] 🌐 Modo standalone/preview → NO se envía sc:ready');
      }
    }, 100);
  }
}
